import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CONTRACT_STATUS_LABELS, type ContractTemplateView, type StaffContractView, contractBlocks } from "@vaulted/shared";
import { useEffect, useState } from "react";
import { errorMessage, get, post } from "../api";
import { useSession } from "../session";
import { Alert, Badge, Button, Checkbox, Dialog, Field, Panel, PanelHeader, SelectField, formatDateTime } from "./ui";

/** A contract's wording as written: headings, paragraphs, bullets; blanks (________) stand out. */
export function ContractText({ text }: { text: string }) {
  const mark = (t: string) =>
    t.split(/(_{4,})/).map((part, i) => (/^_{4,}$/.test(part) ? <span key={i} className="rounded bg-wait-wash px-1 text-wait">{part}</span> : part));
  return (
    <div className="flex flex-col gap-3 text-[15px] leading-relaxed">
      {contractBlocks(text).map((b, i) =>
        b.kind === "heading" ? (
          <h3 key={i} className="mt-2 font-bold">
            {mark(b.text)}
          </h3>
        ) : b.kind === "bullet" ? (
          <p key={i} className="pl-5 -indent-3">
            • {mark(b.text)}
          </p>
        ) : (
          <p key={i}>{mark(b.text)}</p>
        ),
      )}
    </div>
  );
}

function statusBadge(c: StaffContractView) {
  const tone = c.status === "signed" ? "ok" : c.status === "sent" || c.status === "staff_signed" ? "wait" : "off";
  return <Badge tone={tone}>{CONTRACT_STATUS_LABELS[c.status]}</Badge>;
}

function OpenContractPdf({ id }: { id: string }) {
  const open = useMutation({
    mutationFn: async () => {
      const w = window.open("", "_blank");
      const { url } = await get<{ url: string }>(`/api/staff-contracts/${id}/pdf`);
      if (w) w.location.href = url;
      else window.location.href = url;
    },
  });
  return (
    <Button variant="quiet" busy={open.isPending} onClick={() => open.mutate()}>
      Open PDF
    </Button>
  );
}

/** Read the contract and sign it (the staff member, or the clinic signer countersigning). */
export function SignContractDialog({ c, as, onClose, onDone }: { c: StaffContractView; as: "staff" | "clinic"; onClose: () => void; onDone: () => void }) {
  const [agree, setAgree] = useState(false);
  const sign = useMutation({
    mutationFn: () => post(`/api/staff-contracts/${c.id}/${as === "staff" ? "sign" : "countersign"}`, { agree: true }),
    onSuccess: onDone,
  });
  return (
    <Dialog
      title={c.title}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Not now
          </Button>
          <Button variant="primary" disabled={!agree} busy={sign.isPending} onClick={() => sign.mutate()}>
            Sign
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {sign.error != null && <Alert>{errorMessage(sign.error)}</Alert>}
        {as === "clinic" && c.staffSignature && (
          <Alert tone="info">
            {c.staffName} signed this {formatDateTime(c.staffSignature.at)}. You're signing for the clinic.
          </Alert>
        )}
        <div className="max-h-[55vh] overflow-y-auto rounded-xl border border-line p-4">
          <ContractText text={c.body} />
        </div>
        <Checkbox
          label={as === "staff" ? "I have read this agreement and agree to its terms, and I sign it electronically" : "I sign this agreement electronically on behalf of the clinic"}
          description="Your saved signature (Your account → Your signature) goes on the PDF, or your typed name if you don't have one."
          checked={agree}
          onChange={setAgree}
        />
      </div>
    </Dialog>
  );
}

/** Your account → Your contract. */
export function MyContracts() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["my-contracts"], queryFn: () => get<{ mine: StaffContractView[]; toCountersign: StaffContractView[] }>("/api/me/contracts") });
  const [signing, setSigning] = useState<{ c: StaffContractView; as: "staff" | "clinic" } | null>(null);
  const loaded = !!q.data;
  // Opened from the text or Today (…/account#contract): scroll to it once it's on the page.
  useEffect(() => {
    if (loaded && window.location.hash === "#contract") document.getElementById("contract")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [loaded]);
  if (!q.data || (!q.data.mine.length && !q.data.toCountersign.length)) return null;
  const done = async () => {
    setSigning(null);
    await qc.invalidateQueries({ queryKey: ["my-contracts"] });
    await qc.invalidateQueries({ queryKey: ["me"] });
  };
  return (
    <Panel>
      <div id="contract" />
      <PanelHeader title="Your contract" description="Read and sign it here. Once the clinic signs too, a PDF with both signatures is saved for you." />
      <div className="flex flex-col gap-3 px-5 py-5">
        {q.data.mine.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center gap-3">
            <span className="font-semibold">{c.title}</span>
            {statusBadge(c)}
            <span className="text-sm text-muted">sent {formatDateTime(c.sentAt)}</span>
            {c.status === "sent" && (
              <Button variant="primary" onClick={() => setSigning({ c, as: "staff" })}>
                Read and sign
              </Button>
            )}
            {c.hasPdf && <OpenContractPdf id={c.id} />}
          </div>
        ))}
        {q.data.toCountersign.length > 0 && (
          <div className="mt-2 flex flex-col gap-2 border-t border-line pt-3">
            <div className="text-sm font-semibold text-ink-soft">Waiting for you to sign for the clinic</div>
            {q.data.toCountersign.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-3">
                <span>
                  {c.staffName}: {c.title}
                </span>
                <Button variant="primary" onClick={() => setSigning({ c, as: "clinic" })}>
                  Read and sign
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      {signing && <SignContractDialog c={signing.c} as={signing.as} onClose={() => setSigning(null)} onDone={done} />}
    </Panel>
  );
}

/** Staff → name → Contract (Manage payouts): send one, see where it stands, open the signed PDF. */
export function StaffContractPanel({ staffId, name }: { staffId: string; name: string }) {
  const qc = useQueryClient();
  const { me } = useSession();
  const list = useQuery({ queryKey: ["staff-contracts", staffId], queryFn: () => get<StaffContractView[]>(`/api/staff/${staffId}/contracts`) });
  const setup = useQuery({ queryKey: ["contract-templates"], queryFn: () => get<{ templates: ContractTemplateView[]; signers: { id: string; name: string }[] }>("/api/staff-contracts/templates") });
  const [sending, setSending] = useState(false);
  const [signing, setSigning] = useState<StaffContractView | null>(null);
  const setList = (d: StaffContractView[]) => qc.setQueryData(["staff-contracts", staffId], d);
  const cancel = useMutation({ mutationFn: (id: string) => post<StaffContractView[]>(`/api/staff-contracts/${id}/cancel`), onSuccess: setList });
  const [texted, setTexted] = useState<string | null>(null);
  const remind = useMutation({ mutationFn: (id: string) => post<{ textProblem: string | null }>(`/api/staff-contracts/${id}/remind`), onSuccess: (d) => setTexted(d.textProblem ?? `Texted ${name} the link again.`) });
  if (!list.data) return null;
  const shown = list.data.filter((c) => c.status !== "cancelled");
  const templates = setup.data?.templates.filter((t) => !t.archived) ?? [];
  return (
    <Panel className="mt-6">
      <PanelHeader
        title="Contract"
        description={`Sending texts ${name} a link and puts a reminder on their Today; they read and sign it under Your account, then the clinic signer signs it too, and a PDF of both is saved here.`}
        action={
          <Button variant={shown.length ? "quiet" : "primary"} disabled={!templates.length} onClick={() => setSending(true)}>
            {shown.length ? "Send a new contract" : "Send a contract"}
          </Button>
        }
      />
      <div className="flex flex-col gap-3 px-5 py-5">
        {!templates.length && setup.data && <p className="text-sm text-muted">Add the contract's wording under Settings → Staff contracts first.</p>}
        {cancel.error != null && <Alert>{errorMessage(cancel.error)}</Alert>}
        {remind.error != null && <Alert>{errorMessage(remind.error)}</Alert>}
        {texted && <Alert tone="info">{texted}</Alert>}
        {!shown.length && <p className="text-sm text-muted">No contract yet.</p>}
        {shown.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center gap-3">
            <span className="font-semibold">
              {c.title} <span className="text-sm font-normal text-muted">v{c.templateVersion}</span>
            </span>
            {statusBadge(c)}
            <span className="text-sm text-muted">
              sent {formatDateTime(c.sentAt)}
              {c.sentByName ? ` by ${c.sentByName}` : ""} · clinic signer {c.signerName}
            </span>
            {c.status === "staff_signed" && c.signerStaffId === me?.staff.id && (
              <Button variant="primary" onClick={() => setSigning(c)}>
                Sign for the clinic
              </Button>
            )}
            {c.status === "sent" && (
              <Button variant="quiet" busy={remind.isPending} onClick={() => remind.mutate(c.id)}>
                Text them again
              </Button>
            )}
            {(c.status === "sent" || c.status === "staff_signed") && (
              <Button variant="quiet" busy={cancel.isPending} onClick={() => window.confirm("Cancel this contract? It can't be signed after.") && cancel.mutate(c.id)}>
                Cancel
              </Button>
            )}
            {c.hasPdf && <OpenContractPdf id={c.id} />}
          </div>
        ))}
      </div>
      {sending && setup.data && (
        <SendContract
          staffId={staffId}
          templates={templates}
          signers={setup.data.signers}
          onClose={() => setSending(false)}
          onDone={(d) => {
            setList(d.contracts);
            setTexted(d.textProblem ?? `Sent. Texted ${name} a link to read and sign it.`);
            setSending(false);
          }}
        />
      )}
      {signing && (
        <SignContractDialog
          c={signing}
          as="clinic"
          onClose={() => setSigning(null)}
          onDone={async () => {
            setSigning(null);
            await qc.invalidateQueries({ queryKey: ["staff-contracts", staffId] });
            await qc.invalidateQueries({ queryKey: ["me"] });
          }}
        />
      )}
    </Panel>
  );
}

function SendContract({ staffId, templates, signers, onClose, onDone }: { staffId: string; templates: ContractTemplateView[]; signers: { id: string; name: string }[]; onClose: () => void; onDone: (d: { contracts: StaffContractView[]; textProblem: string | null }) => void }) {
  const { me } = useSession();
  const [templateId, setTemplateId] = useState(templates[0]?.id ?? "");
  const [startDate, setStartDate] = useState("");
  const [signer, setSigner] = useState(signers.find((s) => s.id === me?.staff.id)?.id ?? signers[0]?.id ?? "");
  const t = templates.find((x) => x.id === templateId);
  const preview = useQuery({
    queryKey: ["contract-preview", templateId, staffId, signer, startDate, t?.version],
    queryFn: () => post<{ text: string; missing: string[]; unknown: string[] }>("/api/staff-contracts/preview", { body: t?.body ?? "", staffMemberId: staffId, signerStaffId: signer || null, startDate: startDate || null }),
    enabled: !!t,
  });
  const send = useMutation({ mutationFn: () => post<{ contracts: StaffContractView[]; textProblem: string | null }>(`/api/staff/${staffId}/contracts`, { templateId, startDate: startDate || null, signerStaffId: signer }), onSuccess: onDone });
  return (
    <Dialog
      title="Send a contract"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={send.isPending} disabled={!t || !signer || !!preview.data?.unknown.length} onClick={() => send.mutate()}>
            Send to sign
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {send.error != null && <Alert>{errorMessage(send.error)}</Alert>}
        <div className="grid gap-3 sm:grid-cols-3">
          <SelectField label="Contract" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            {templates.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name} (v{x.version})
              </option>
            ))}
          </SelectField>
          <Field label="Start date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <SelectField label="Signs for the clinic" value={signer} onChange={(e) => setSigner(e.target.value)}>
            {signers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </SelectField>
        </div>
        {preview.data?.unknown.length ? <Alert>These aren't placeholders; fix them in the template: {preview.data.unknown.map((u) => `{{${u}}}`).join(", ")}</Alert> : null}
        {preview.data?.missing.length ? (
          <Alert tone="info">No value yet for {preview.data.missing.map((m) => `{{${m}}}`).join(", ")}; it shows as a blank (________). Fill it in (their W-9 gives the address), or send it as is.</Alert>
        ) : null}
        <div className="max-h-[45vh] overflow-y-auto rounded-xl border border-line p-4">{preview.data ? <ContractText text={preview.data.text} /> : <p className="text-sm text-muted">Loading the preview…</p>}</div>
      </div>
    </Dialog>
  );
}
